import axios from 'axios';
import React, { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaCheckCircle, FaEye, FaTruck, FaTimesCircle, FaArrowLeft, FaShoppingCart, FaSearch } from 'react-icons/fa';
import LoadingScreen from '../../components/LoadingScreen';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3500';

function ShippedOrdersPage() {
    const [orders, setOrders] = useState([]);
    const [filteredOrders, setFilteredOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [updatingStatus, setUpdatingStatus] = useState(null);
    const [sortOrder, setSortOrder] = useState('newest'); // 'newest', 'oldest'
    const [searchTerm, setSearchTerm] = useState('');
    const [counts, setCounts] = useState({
        pendingOrdersCount: 0,
        confirmedOrdersCount: 0,
        shippedOrdersCount: 0,
        deliveredOrdersCount: 0,
        cancelledOrdersCount: 0
    });

    const handleApiError = (error) => {
        console.error('API Error details:', error);
        alert(error.response?.data?.message || 'Something went wrong. Please try again.');
    };

    const applySearchAndSort = (rawOrders, query, sort) => {
        const lowerCaseQuery = query.toLowerCase().trim();
        let filtered = rawOrders;
        if (lowerCaseQuery) {
            filtered = rawOrders.filter(item => {
                const name = item.address?.name?.toLowerCase() || '';
                const mobile = item.mobile ? String(item.mobile).toLowerCase() : '';
                const token = item.token?.toLowerCase() || '';
                const house = item.address?.house?.toLowerCase() || '';
                const area = item.address?.area?.toLowerCase() || '';
                const amount = item.totalAmount?.toString() || '';
                const grandAmount = item.grandTotal?.toString() || '';
                
                return name.includes(lowerCaseQuery) || 
                       mobile.includes(lowerCaseQuery) || 
                       token.includes(lowerCaseQuery) || 
                       house.includes(lowerCaseQuery) || 
                       area.includes(lowerCaseQuery) || 
                       amount.includes(lowerCaseQuery) ||
                       grandAmount.includes(lowerCaseQuery);
            });
        }
        
        const sorted = [...filtered].sort((a, b) => {
            const dateA = new Date(a.createdAt);
            const dateB = new Date(b.createdAt);
            return sort === 'newest' ? dateB - dateA : dateA - dateB;
        });
        
        setFilteredOrders(sorted);
    };

    const fetchShippedOrders = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('authToken');
            const [ordersRes, countsRes] = await Promise.all([
                axios.get(`${API_BASE_URL}/shipped-orders`, {
                    headers: { Authorization: `Bearer ${token}` }
                }),
                axios.get(`${API_BASE_URL}/counts`, {
                    headers: { Authorization: `Bearer ${token}` }
                })
            ]);
            setOrders(ordersRes.data);
            applySearchAndSort(ordersRes.data, searchTerm, sortOrder);
            setCounts(countsRes.data);
        } catch (error) {
            handleApiError(error);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (query) => {
        setSearchTerm(query);
        applySearchAndSort(orders, query, sortOrder);
    };

    const handleSortChange = (newSortOrder) => {
        setSortOrder(newSortOrder);
        applySearchAndSort(orders, searchTerm, newSortOrder);
    };

    const handleBackButtonClick = () => {
        setSearchTerm('');
        setSortOrder('newest');
        applySearchAndSort(orders, '', 'newest');
    };

    const updateOrderStatus = async (orderId, newStatus) => {
        setUpdatingStatus(orderId);
        try {
            const token = localStorage.getItem('authToken');
            await axios.put(`${API_BASE_URL}/update-order-status/${orderId}`, 
                { status: newStatus },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            // Refresh the orders list
            await fetchShippedOrders();
            alert(`Order status updated to ${newStatus}`);
        } catch (error) {
            handleApiError(error);
        } finally {
            setUpdatingStatus(null);
        }
    };

    useEffect(() => {
        fetchShippedOrders();
    }, []);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0
        }).format(amount);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const orderStatusTiles = [
        { status: 'pending', title: 'PENDING ORDERS', count: counts.pendingOrdersCount, link: '/admin/pending-orders', icon: FaShoppingCart },
        { status: 'confirmed', title: 'CONFIRMED ORDERS', count: counts.confirmedOrdersCount, link: '/admin/confirmed-orders', icon: FaCheckCircle },
        { status: 'shipped', title: 'SHIPPED ORDERS', count: counts.shippedOrdersCount, link: '/admin/shipped-orders', icon: FaTruck },
        { status: 'delivered', title: 'DELIVERED ORDERS', count: counts.deliveredOrdersCount, link: '/admin/delivered-orders', icon: FaCheckCircle },
        { status: 'cancelled', title: 'CANCELLED ORDERS', count: counts.cancelledOrdersCount, link: '/admin/cancelled-orders', icon: FaTimesCircle }
    ];

    const totalOrders = orderStatusTiles.reduce((s, t) => s + (t.count || 0), 0);

    return (
        <Fragment>
            <div className="container-fluid p-3 pt-2 g-0 adma">
                <div className="row mb-3">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center">
                            <div className="section-title">
                                <h4 className="mt-1">SHIPPED ORDERS</h4>
                                <p className="text-muted p-0 m-0">Manage orders that are currently in transit to customers</p>
                            </div>
                            <Link 
                                to="/admin/orders" 
                                className="back-btn-pill"
                                title="Back to Orders"
                                onClick={handleBackButtonClick}
                            >
                                <FaArrowLeft />
                                Back to Orders
                            </Link>
                        </div>
                    </div>
                </div>

                <div className="row g-0 order-tiles-row mb-2">
                    {orderStatusTiles.map((item) => {
                        const Icon = item.icon;
                        const pct = totalOrders ? ((item.count / totalOrders) * 100).toFixed(1) : 0;
                        const isActive = item.status === 'shipped';
                        return (
                            <div key={item.status} className="col-12 col-sm-6 col-md-4 col-xl">
                                <Link to={item.link} className={`order-tile-card order-tile-${item.status} ${isActive ? 'active' : ''} text-decoration-none`}>
                                    <div className="order-tile-top d-flex justify-content-between align-items-start mb-2">
                                        <div className="order-tile-icon-wrapper"><Icon className="order-tile-icon" /></div>
                                        <span className="order-tile-count">{item.count}</span>
                                    </div>
                                    <h6 className="order-tile-title mb-1">{item.title}</h6>
                                    <div className="order-tile-progress mt-2" aria-label="Status share" aria-valuenow={pct} aria-valuemin="0" aria-valuemax="100">
                                        <div className="order-tile-progress-bar" style={{ width: pct + '%' }}></div>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center mt-3 small">
                                        <span className="text-muted">{pct}%</span>
                                        <span className="order-tile-link-label">VIEW</span>
                                    </div>
                                </Link>
                            </div>
                        );
                    })}
                </div>

                {orders.length > 0 && (
                    <div className="pending-control-panel" style={{ position: 'relative', zIndex: 1 }}>
                        <div className="search-control-wrapper flex-grow-1" style={{ maxWidth: '450px' }}>
                            <input
                                type="text"
                                className="form-control search-input-custom w-100"
                                placeholder="Search by name, token, mobile, address..."
                                value={searchTerm}
                                onChange={(e) => handleSearch(e.target.value)}
                            />
                            <FaSearch className="search-icon-custom" />
                        </div>
                        
                        <div className="d-flex align-items-center gap-3">
                            <span className="text-muted small fw-semibold text-nowrap m-0">Sort by:</span>
                            <div className="sort-buttons-group">
                                <button
                                    type="button"
                                    className={`sort-btn-custom ${sortOrder === 'newest' ? 'active' : ''}`}
                                    onClick={() => handleSortChange('newest')}
                                >
                                    Newest First
                                </button>
                                <button
                                    type="button"
                                    className={`sort-btn-custom ${sortOrder === 'oldest' ? 'active' : ''}`}
                                    onClick={() => handleSortChange('oldest')}
                                >
                                    Oldest First
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {loading ? (
                    <LoadingScreen message="Loading shipped orders..." />
                ) : (
                    <div className="row g-0">
                        <div className="col-12">
                            {orders.length > 0 ? (
                                <div className="card border-0 rounded-0 shadow-none order-table-card" style={{ position: 'relative', zIndex: 2 }}>
                                    <div className="card-body p-0">
                                        <div className="table-responsive" style={{ overflow: 'visible' }}>
                                            <table className="table table-hover mb-0">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th className="border-0">Sr. No.</th>
                                                        <th className="border-0">Order Info</th>
                                                        <th className="border-0">Customer Details</th>
                                                        <th className="border-0">Items & Amount</th>
                                                        <th className="border-0">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {filteredOrders.length > 0 ? (
                                                        filteredOrders.map((item, index) => (
                                                            <tr key={item._id}>
                                                                {/* Column 0: Sr. No. */}
                                                                <td className="align-middle text-muted small fw-semibold">
                                                                    {index + 1}
                                                                </td>

                                                                {/* Column 1: Order Info */}
                                                                <td className="align-middle">
                                                                    <div>
                                                                        <span className="fw-bold text-dark">
                                                                            {item.token ? item.token.toUpperCase() : 'N/A'}
                                                                        </span>
                                                                        <div className="text-muted small mt-1">
                                                                            {formatDate(item.createdAt)}
                                                                        </div>
                                                                    </div>
                                                                </td>

                                                                {/* Column 2: Customer Details */}
                                                                <td className="align-middle">
                                                                    <div>
                                                                        <strong className="text-dark">{item.address?.name || 'N/A'}</strong>
                                                                        <div className="text-muted small mt-1">
                                                                            <span className="text-primary fw-semibold">{item.mobile || 'N/A'}</span>
                                                                            {item.address && (
                                                                                <>
                                                                                    <span className="mx-2 text-muted">|</span>
                                                                                    <span>{item.address.house}, {item.address.area}</span>
                                                                                </>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </td>

                                                                {/* Column 3: Items & Amount */}
                                                                <td className="align-middle">
                                                                    <div>
                                                                        <strong className="text-success fs-6">
                                                                            {formatCurrency((item.grandTotal !== undefined && item.grandTotal !== null) ? item.grandTotal : (item.totalAmount || 0))}
                                                                        </strong>
                                                                        <div className="mt-1">
                                                                            <div className="item-count-hover-container">
                                                                                <span className="item-count-trigger text-muted small">
                                                                                    {item.items?.length || 0} {item.items?.length === 1 ? 'item' : 'items'}
                                                                                </span>
                                                                                {item.items && item.items.length > 0 && (
                                                                                    <div className="item-names-popover shadow-sm" style={{ zIndex: 9999 }}>
                                                                                        <div className="popover-header fw-bold border-bottom pb-1 mb-2 text-dark" style={{ fontSize: '0.78rem' }}>ORDER ITEMS</div>
                                                                                        <div className="popover-items-list scrollbar-custom" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                                                                                            {item.items.map((prod, pIdx) => (
                                                                                                <div key={pIdx} className="popover-item d-flex justify-content-between gap-3 text-secondary py-1" style={{ fontSize: '0.75rem', borderBottom: pIdx < item.items.length - 1 ? '1px dashed #f1f5f9' : 'none' }}>
                                                                                                    <span className="text-start">{prod.name}</span>
                                                                                                    <span className="text-end fw-bold text-nowrap">x {prod.quantity}</span>
                                                                                                </div>
                                                                                            ))}
                                                                                        </div>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                                <td className="align-middle">
                                                                    <div className="order-actions-wrapper">
                                                                        <Link 
                                                                            to={`/admin/orders-details/${item._id}`} 
                                                                            className="order-action-btn order-action-btn-view"
                                                                            title="View Details"
                                                                        >
                                                                            <FaEye />
                                                                        </Link>
                                                                        <button
                                                                            className="order-action-btn order-action-btn-accept"
                                                                            onClick={() => updateOrderStatus(item._id, 'delivered')}
                                                                            disabled={updatingStatus === item._id}
                                                                            title="Mark as Delivered"
                                                                        >
                                                                            {updatingStatus === item._id ? (
                                                                                <span className="spinner-border spinner-border-sm text-success" />
                                                                            ) : (
                                                                                <FaCheckCircle />
                                                                            )}
                                                                        </button>
                                                                        <button
                                                                            className="order-action-btn order-action-btn-cancel"
                                                                            onClick={() => updateOrderStatus(item._id, 'cancelled')}
                                                                            disabled={updatingStatus === item._id}
                                                                            title="Cancel Order"
                                                                        >
                                                                            {updatingStatus === item._id ? (
                                                                                <span className="spinner-border spinner-border-sm text-danger" />
                                                                            ) : (
                                                                                <FaTimesCircle />
                                                                            )}
                                                                        </button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <tr>
                                                            <td colSpan="5" className="text-center py-4 text-muted fw-medium">
                                                                No matching orders found.
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="order-empty-state">
                                    <div className="order-empty-state-icon-wrapper">
                                        <FaTruck className="order-empty-state-icon" />
                                    </div>
                                    <h5>No Shipped Orders</h5>
                                    <p>No orders are currently in transit. View confirmed orders to start shipping them.</p>
                                    <Link 
                                        to="/admin/confirmed-orders"
                                        className="order-empty-state-btn"
                                    >
                                        View Confirmed Orders
                                    </Link>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            <style>{`
                .pending-control-panel {
                    position: relative;
                    z-index: 1;
                }
                .order-table-card {
                    position: relative;
                    z-index: 2; /* Bring table container above control panel */
                }
                .item-count-hover-container {
                    position: relative;
                    display: inline-block;
                }
                .item-count-hover-container:hover {
                    z-index: 9999; /* Bring hovered cell stack to front */
                }
                .item-count-trigger {
                    cursor: pointer;
                    border-bottom: 1px dashed #cbd5e1;
                    padding-bottom: 1px;
                    transition: color 0.15s ease;
                }
                .item-count-trigger:hover {
                    color: #ed3237 !important;
                }
                .item-names-popover {
                    display: none;
                    position: absolute;
                    bottom: calc(100% + 8px); /* Position upside */
                    left: 50%;
                    transform: translateX(-50%);
                    background: #ffffff;
                    border: 1px solid #cbd5e1;
                    padding: 10px 14px;
                    z-index: 9999; /* Raise popover above all headers and controls */
                    width: max-content;
                    min-width: 200px;
                    max-width: 280px;
                    text-align: left;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
                }
                /* Downward pointing arrow */
                .item-names-popover::after {
                    content: "";
                    position: absolute;
                    top: 100%;
                    left: 50%;
                    transform: translateX(-50%);
                    border-width: 6px;
                    border-style: solid;
                    border-color: #ffffff transparent transparent transparent;
                }
                .item-names-popover::before {
                    content: "";
                    position: absolute;
                    top: 100%;
                    left: 50%;
                    transform: translateX(-50%);
                    border-width: 7px;
                    border-style: solid;
                    border-color: #cbd5e1 transparent transparent transparent;
                    z-index: -1;
                }
                .item-count-hover-container:hover .item-names-popover {
                    display: block;
                }
            `}</style>
        </Fragment>
    );
}

export default ShippedOrdersPage;
